import React, {useEffect, useState, useRef, useMemo} from 'react'
import { useLocation } from 'react-router-dom'
import { CCard, CCardBody, CCol, CCardHeader, CRow, CContainer, CDropdown,
  CDropdownMenu,
  CDropdownItem,
  CDropdownToggle,
  CSpinner,
  CButton,
  CWidgetStatsA, } from '@coreui/react'
import {
  CChartBar,
  CChartDoughnut,
  CChartLine,
  CChartPie,
} from '@coreui/react-chartjs'
import CIcon from '@coreui/icons-react'
import { getStyle } from '@coreui/utils'
import settingsStore from '../../store/SettingsStore'
import datasStore from '../../store/DataStore'
import cashShiftsStore from '../../store/CashShiftsStore'
import { observer } from 'mobx-react-lite';
import DashboardFilters from '../UI/PeriodFilters/DashboardFilters'
import { formatYmd } from '../../utils/dateRange'
import { chartPeriodAxisCaption } from '../../utils/iikoOlapGroup'
import { Link } from 'react-router-dom'

import { cilOptions, cilChatBubble, cilArrowRight } from '@coreui/icons'
import { logReportView } from '../../api/reportLogApi'
import InsightsPanel from '../Insights/InsightsPanel'
import HermesPanel from '../Insights/HermesPanel'
import DishStatsPanel from '../Insights/DishStatsPanel'
import { buildDashboardInsights } from '../../utils/insightsEngine'
import ErrorAlert from '../UI/Status/ErrorAlert'
import DashboardExtrasSection from './DashboardExtrasSection'
import PinnedInsightsSection from './PinnedInsightsSection'
import periodPrefsStore from '../../store/PeriodPrefsStore'

function chartAxisRange(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return {};
  const nums = arr.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  if (!nums.length) return {};
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min;
  const pad = span > 0 ? span * 0.12 : Math.max(1, Math.abs(max || 0) * 0.12);
  return { min: min - pad, max: max + pad };
}

function lastVal(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const v = Number(arr[arr.length - 1]);
  return Number.isNaN(v) ? null : v;
}

function sumVal(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const nums = arr.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0);
}

function avgVal(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const nums = arr.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function trendPct(arr) {
  if (!Array.isArray(arr) || arr.length < 2) return null;
  const last = toNum(arr[arr.length - 1]);
  const prev = toNum(arr[arr.length - 2]);
  if (last == null || prev == null || prev === 0) return null;
  return ((last - prev) / Math.abs(prev)) * 100;
}

const TREND_TOOLTIP = "Изменение по сравнению с предыдущим днём в выбранном периоде";

function TrendBadge({ arr, invert = false }) {
  const t = trendPct(arr);
  if (t == null) return null;
  const good = invert ? t < 0 : t > 0;
  return (
    <span
      className={`trend-badge ${good ? "trend-badge--up" : "trend-badge--down"}`}
      title={TREND_TOOLTIP}
    >
      {t > 0 ? "↑" : "↓"} {Math.abs(t).toFixed(1)}%
    </span>
  );
}

function fmtInt(v) {
  if (v == null) return "—";
  return Math.round(v).toLocaleString("ru-RU");
}

const MOBILE_KPI_COLOR_VAR = {
  primary: "var(--cui-primary)",
  info: "var(--cui-info)",
  danger: "var(--cui-danger)",
};

function MobileKpiCard({ to, state, color = "primary", label, value, periodLabel, periodValue, trend }) {
  return (
    <Link
      to={to}
      state={state}
      className="mobile-kpi-card text-decoration-none text-white d-flex flex-column"
      style={{ backgroundColor: MOBILE_KPI_COLOR_VAR[color] || MOBILE_KPI_COLOR_VAR.primary }}
    >
      <div className="mobile-kpi-card-label small text-white-50">{label}</div>
      <div className="mobile-kpi-card-value fw-semibold">{value}</div>
      {trend}
      <div className="mobile-kpi-card-period small text-white-50 mt-auto">
        {periodLabel}: {periodValue}
      </div>
    </Link>
  );
}

function toNum(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  if (typeof v === "string") {
    const s = v
      .replace(/\u00A0|\u202F/g, " ")
      .replace(/[%₽]/g, "")
      .replace(/\s+/g, "")
      .replace(",", ".");
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  }
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function makePaletteColors(palette, count) {
  const base = Array.isArray(palette) && palette.length ? palette : ["#0d6efd"];
  return Array.from({ length: count }, (_, i) => base[i % base.length]);
}

function buildPieItems(labels, values, palette) {
  const lab = Array.isArray(labels) ? labels : [];
  const val = Array.isArray(values) ? values : [];
  const items = lab
    .map((label, idx) => ({ label: label ?? "—", value: toNum(val[idx]) ?? 0 }))
    .filter((x) => x.label != null && x.label !== "");
  items.sort((a, b) => b.value - a.value);
  const colors = makePaletteColors(palette, items.length);
  return items.map((it, i) => ({ ...it, color: colors[i] }));
}

function sumNums(values) {
  if (!Array.isArray(values) || values.length === 0) return 0;
  return values
    .map(toNum)
    .filter((n) => n != null && Number.isFinite(n))
    .reduce((a, b) => a + b, 0);
}

const Charts = observer(() => {
  const location = useLocation();
  const [selectedServer, setSelectedServer] = useState('');
  const [quickRange, setQuickRange] = useState("");
  const widgetChartRef1 = useRef(null)
  const [currentDate, setCurrentDate] = useState('');
  const [sevenDaysAgo, setSevenDaysAgo] = useState('');
  // The period actually submitted (button / quick-range / server change), as opposed
  // to datasStore.fromDate/toDate which update live on every date-picker click.
  // Hermes (GigaChat) is billed per call, so it must only re-fetch when a period was
  // really applied — not on every intermediate date the user picks while browsing.
  const [appliedRange, setAppliedRange] = useState({ from: null, to: null });

  const formatDate = (date) => formatYmd(date);

  const loadData = (from, to) => {
    if (!selectedServer || !from || !to) return;
    setAppliedRange({ from, to });
    void Promise.all([
      datasStore.loadPeriodData(selectedServer, from, to),
      cashShiftsStore.fetchShifts(selectedServer, from, to, {
        status: "ANY",
        employeesIncludeDeleted: true,
      }),
    ]);
  };

  const handleGroupByChange = (byRestaurant) => {
    datasStore.setGroupByRestaurant(byRestaurant);
    loadData(datasStore.fromDate ?? sevenDaysAgo, datasStore.toDate ?? currentDate);
  };

  const handleRestaurantGroupChange = (groupName) => {
    datasStore.setSelectedRestaurantGroup(groupName);
    loadData(datasStore.fromDate ?? sevenDaysAgo, datasStore.toDate ?? currentDate);
  };

  const periodCaption = chartPeriodAxisCaption(
    datasStore.openDate,
    datasStore.openDateIso,
    datasStore.groupByRestaurant,
    datasStore.selectedRestaurantGroup,
  );

  const chartSeriesKey = `${datasStore.groupByRestaurant ? datasStore.selectedRestaurantGroup : "all"}-${datasStore.openDateIso.join("|")}`;

  useEffect(() => {
    const init = async () => {
      const today = new Date();
      const cur = formatDate(today);
      const pastDate = new Date(today);
      pastDate.setDate(today.getDate() - 7);
      const past = formatDate(pastDate);
      setCurrentDate(cur);
      setSevenDaysAgo(past);
      datasStore.setFromDate(past);
      datasStore.setToDate(cur);

      const userId = localStorage.getItem("userId");
      if (userId) {
        await settingsStore.getServers(userId);
      }

      logReportView({
        event: "report_view",
        reportKey: "dashboard",
        reportTitle: "Дашборд",
        userId,
        path: "/dashboard",
        at: new Date().toISOString(),
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (selectedServer === "") {
      const fromState = location.state?.serverId;
      setSelectedServer(String(fromState ?? settingsStore.servers[0].id));
    }
    if (location.state?.groupByRestaurant) {
      datasStore.setGroupByRestaurant(true);
    }
    if (location.state?.restaurantGroup) {
      datasStore.setSelectedRestaurantGroup(location.state.restaurantGroup);
    }
  }, [settingsStore.servers, selectedServer, location.state]);

  // Запоминаем выбранный сервер, чтобы другие вкладки (склад, прогноз, меню-
  // инжиниринг и т.п.) открывались с тем же рестораном, а не со сбросом.
  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (!selectedServer || !sevenDaysAgo || !currentDate) return;
    datasStore.loadPeriodData(selectedServer, sevenDaysAgo, currentDate);
    setAppliedRange({ from: sevenDaysAgo, to: currentDate });
  }, [selectedServer, sevenDaysAgo, currentDate, datasStore.groupByRestaurant]);

  const groupDataPending =
    datasStore.groupByRestaurant &&
    datasStore.restaurantGroups.length === 0 &&
    !datasStore.loading;

  const handleRefresh = (from, to) => {
    const f = from ?? datasStore.fromDate ?? sevenDaysAgo;
    const t = to ?? datasStore.toDate ?? currentDate;
    loadData(f, t);
  };

  const handleServerChange = (e) => {
    setSelectedServer(e.target.value);
  };

  const totalRevenue = sumVal(datasStore.dishDiscountSumInt) || 0;
  const totalOrders = sumVal(datasStore.uniqOrderId) || 0;
  const totalItems = sumVal(datasStore.orderItems) || 0;
  const totalReturns = sumVal(datasStore.dishReturnSum) || 0;
  const itemsPerCheck = totalOrders > 0 ? totalItems / totalOrders : null;
  const returnPct = totalRevenue > 0 ? (totalReturns / totalRevenue) * 100 : null;

  const payPalette = ['#41B883', '#E46651', '#00D8FF', '#DD1B16', '#23e033ff', '#32aebeff', '#d15bd1ff'];
  const categoryPalette = ['#FF6384', '#36A2EB', '#FFCE56', '#ff2e12ff', '#23e033ff', '#32aebeff', '#d15bd1ff'];

  const payTypeItems = buildPieItems(datasStore.payTypesName, datasStore.payTypesSum, payPalette);
  const payTypesTotal = sumNums(payTypeItems.map((x) => x.value));

  const categoryItems = buildPieItems(datasStore.categoryName, datasStore.categorySale, categoryPalette);
  const categoryTotal = sumNums(categoryItems.map((x) => x.value));

  const dashboardInsights = useMemo(() => {
    if (datasStore.loading || !datasStore.openDate?.length) return [];
    return buildDashboardInsights(datasStore, { shifts: cashShiftsStore.shifts });
  }, [
    chartSeriesKey,
    datasStore.loading,
    datasStore.payTypesName,
    datasStore.categoryName,
    cashShiftsStore.shifts,
  ]);

  const dailyItemsPerCheck = useMemo(() => {
    const items = datasStore.orderItems || [];
    const orders = datasStore.uniqOrderId || [];
    return items.map((it, i) => {
      const o = toNum(orders[i]) || 0;
      return o > 0 ? (toNum(it) || 0) / o : 0;
    });
  }, [chartSeriesKey]);

  const dailyReturnPctSeries = useMemo(() => {
    const rev = datasStore.dishDiscountSumInt || [];
    const ret = datasStore.dishReturnSum || [];
    return rev.map((r, i) => {
      const rv = toNum(r) || 0;
      const rt = toNum(ret[i]) || 0;
      return rv > 0 ? (rt / rv) * 100 : 0;
    });
  }, [chartSeriesKey]);

  const lastDayReturnPct = lastVal(dailyReturnPctSeries);
  const lastDayItemsPerCheck = lastVal(dailyItemsPerCheck);

  const navState = { serverId: selectedServer };
  const mobileKpiItems = [
    {
      key: "avgcheck",
      to: "/avgcheckdetails",
      color: "primary",
      label: "Средний чек",
      value: `${fmtInt(lastVal(datasStore.dishDiscountSumIntAverage))} ₽`,
      periodLabel: "За период",
      periodValue: `${fmtInt(avgVal(datasStore.dishDiscountSumIntAverage))} ₽`,
      trend: <TrendBadge arr={datasStore.dishDiscountSumIntAverage} />,
    },
    {
      key: "revenue",
      to: "/dishdetails",
      color: "info",
      label: "Выручка",
      value: `${fmtInt(lastVal(datasStore.dishDiscountSumInt))} ₽`,
      periodLabel: "За период",
      periodValue: `${fmtInt(sumVal(datasStore.dishDiscountSumInt))} ₽`,
      trend: <TrendBadge arr={datasStore.dishDiscountSumInt} />,
    },
    {
      key: "checks",
      to: "/checkdetails",
      color: "danger",
      label: "Чеков",
      value: fmtInt(lastVal(datasStore.uniqOrderId)),
      periodLabel: "За период",
      periodValue: fmtInt(sumVal(datasStore.uniqOrderId)),
      trend: <TrendBadge arr={datasStore.uniqOrderId} />,
    },
    {
      key: "guests",
      to: "/guestsdetails",
      color: "primary",
      label: "Гостей",
      value: fmtInt(lastVal(datasStore.guestNum)),
      periodLabel: "За период",
      periodValue: fmtInt(sumVal(datasStore.guestNum)),
      trend: <TrendBadge arr={datasStore.guestNum} />,
    },
    {
      key: "cost",
      to: "/costdetails",
      color: "primary",
      label: "Себестоимость",
      value: `${fmtInt(lastVal(datasStore.productCostBasePercent))}%`,
      periodLabel: "За период",
      periodValue: `${fmtInt(avgVal(datasStore.productCostBasePercent))}%`,
      trend: <TrendBadge arr={datasStore.productCostBasePercent} invert />,
    },
    {
      key: "markup",
      to: "/markupdetails",
      color: "info",
      label: "Наценка",
      value: `${fmtInt(lastVal(datasStore.productCostBaseMarkUp))}%`,
      periodLabel: "За период",
      periodValue: `${fmtInt(avgVal(datasStore.productCostBaseMarkUp))}%`,
      trend: <TrendBadge arr={datasStore.productCostBaseMarkUp} />,
    },
    {
      key: "avgbyguest",
      to: "/avgbyguestdetails",
      color: "danger",
      label: "Ср. выручка с гостя",
      value: `${fmtInt(lastVal(datasStore.averageByGuest))} ₽`,
      periodLabel: "За период",
      periodValue: `${fmtInt(avgVal(datasStore.averageByGuest))} ₽`,
      trend: <TrendBadge arr={datasStore.averageByGuest} />,
    },
    {
      key: "returns",
      to: "/returndetails",
      color: "primary",
      label: "Сумма возвратов",
      value: `${fmtInt(lastVal(datasStore.dishReturnSum))} ₽`,
      periodLabel: "За период",
      periodValue: `${fmtInt(sumVal(datasStore.dishReturnSum))} ₽`,
      trend: <TrendBadge arr={datasStore.dishReturnSum} invert />,
    },
    {
      key: "itemsPerCheck",
      to: "/itemsdetails",
      color: "primary",
      label: "Позиций на чек",
      value: lastDayItemsPerCheck != null ? lastDayItemsPerCheck.toFixed(2) : "—",
      periodLabel: "За период",
      periodValue: itemsPerCheck != null ? itemsPerCheck.toFixed(2) : "—",
      trend: <TrendBadge arr={dailyItemsPerCheck} />,
    },
    {
      key: "returnPct",
      to: "/returndetails",
      color: "primary",
      label: "% возвратов от выручки",
      value: lastDayReturnPct != null ? `${lastDayReturnPct.toFixed(2)}%` : "—",
      periodLabel: "За период",
      periodValue: returnPct != null ? `${returnPct.toFixed(2)}%` : "—",
      trend: <TrendBadge arr={dailyReturnPctSeries} invert />,
    },
    {
      key: "avgChecksPerDay",
      to: "/checkdetails",
      color: "primary",
      label: "Ср. чеков в день",
      value: fmtInt(lastVal(datasStore.uniqOrderId)),
      periodLabel: "За период",
      periodValue:
        datasStore.uniqOrderId.length > 0
          ? `${fmtInt(totalOrders / datasStore.uniqOrderId.length)}/день`
          : "—",
      trend: <TrendBadge arr={datasStore.uniqOrderId} />,
    },
  ];

  return (


    <CContainer>
        <CContainer className='mb-4'>
      <DashboardFilters
        servers={settingsStore.servers}
        selectedServer={selectedServer}
        onServerChange={handleServerChange}
        fromDate={datasStore.fromDate ?? sevenDaysAgo}
        toDate={datasStore.toDate ?? currentDate}
        onFromChange={(d) => datasStore.setFromDate(d)}
        onToChange={(d) => datasStore.setToDate(d)}
        onRefresh={handleRefresh}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        showGroupBy
        groupByRestaurant={datasStore.groupByRestaurant}
        onGroupByChange={handleGroupByChange}
        restaurantGroups={datasStore.restaurantGroups}
        selectedRestaurantGroup={datasStore.selectedRestaurantGroup}
        onRestaurantGroupChange={handleRestaurantGroupChange}
        groupDataPending={groupDataPending}
        disabled={datasStore.loading}
        loading={datasStore.loading}
      />
    </CContainer>
      <ErrorAlert error={datasStore.error} title="Ошибка загрузки данных" />

      <CCard className="mb-4 border-0 text-white" style={{ background: "linear-gradient(135deg, var(--cui-primary), var(--cui-primary-text-emphasis))" }}>
        <CCardBody className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
          <div className="d-flex align-items-center gap-3">
            <CIcon icon={cilChatBubble} size="xl" />
            <div>
              <div className="fw-semibold">AI-помощник</div>
              <div className="small opacity-75">
                Спросите про выручку, официантов, меню или кассовую дисциплину — помощник сам найдёт нужные данные.
              </div>
            </div>
          </div>
          <CButton as={Link} to="/assistant" color="light" className="text-nowrap">
            Перейти в чат <CIcon icon={cilArrowRight} className="ms-1" />
          </CButton>
        </CCardBody>
      </CCard>

      <HermesPanel
        serverId={selectedServer}
        dateFrom={appliedRange.from}
        dateTo={appliedRange.to}
        groupByRestaurant={datasStore.groupByRestaurant}
        restaurantGroup={datasStore.selectedRestaurantGroup}
      />
      {/* Temporarily disabled per product request — heuristic "умные подсказки"
          overlapped with the real Hermes/AI-помощник analysis. Re-enable by
          restoring this line. */}
      {false && <InsightsPanel insights={dashboardInsights} />}

      <PinnedInsightsSection serverId={selectedServer} />
      <DashboardExtrasSection serverId={selectedServer} />

      {datasStore.loading && datasStore.openDate.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center py-5 text-body-secondary mb-4">
          <CSpinner className="me-2" />
          Загрузка дашборда…
        </div>
      ) : (
        <>
      <div className="dash-mobile-kpi-grid d-lg-none mb-4">
        {mobileKpiItems.map((item) => (
          <MobileKpiCard
            key={item.key}
            to={item.to}
            state={navState}
            color={item.color}
            label={item.label}
            value={item.value}
            periodLabel={item.periodLabel}
            periodValue={item.periodValue}
            trend={item.trend}
          />
        ))}
      </div>

      <div className="d-none d-lg-block">
        <CRow className='mb-5 g-4' key={chartSeriesKey}>

            <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="primary"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Средний чек</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.dishDiscountSumIntAverage))} &#8381;
                      </span>
                      <TrendBadge arr={datasStore.dishDiscountSumIntAverage} />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(avgVal(datasStore.dishDiscountSumIntAverage))} &#8381;
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/avgcheckdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Средний чек',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-primary'),
                        data: datasStore.dishDiscountSumIntAverage,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.dishDiscountSumIntAverage),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
             <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="info"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Выручка</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.dishDiscountSumInt))} &#8381;
                      </span>
                      <TrendBadge arr={datasStore.dishDiscountSumInt} />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(sumVal(datasStore.dishDiscountSumInt))} &#8381;
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem
                      as={Link}
                      to="/dishdetails"
                      state={{ serverId: selectedServer }}
                    >
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  ref={widgetChartRef1}
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Выручка',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-info'),
                        data: datasStore.dishDiscountSumInt,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.dishDiscountSumInt),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="danger"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Чеков</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.uniqOrderId))}
                      </span>
                      <TrendBadge arr={datasStore.uniqOrderId} />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(sumVal(datasStore.uniqOrderId))}
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/checkdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  ref={widgetChartRef1}
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Чеков',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-danger'),
                        data: datasStore.uniqOrderId,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.uniqOrderId),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="primary"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Гостей</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.guestNum))}
                      </span>
                      <TrendBadge arr={datasStore.guestNum} />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">{fmtInt(sumVal(datasStore.guestNum))}</span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/guestsdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  ref={widgetChartRef1}
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Чеков',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-primary'),
                        data: datasStore.guestNum,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.guestNum),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="primary"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Себестоимость</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.productCostBasePercent))}%
                      </span>
                      <TrendBadge arr={datasStore.productCostBasePercent} invert />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(avgVal(datasStore.productCostBasePercent))}%
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/costdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Себестоимость',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-primary'),
                        data: datasStore.productCostBasePercent,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.productCostBasePercent),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="info"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Наценка</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.productCostBaseMarkUp))}%
                      </span>
                      <TrendBadge arr={datasStore.productCostBaseMarkUp} />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(avgVal(datasStore.productCostBaseMarkUp))}%
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/markupdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Наценка',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-info'),
                        data: datasStore.productCostBaseMarkUp,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.productCostBaseMarkUp),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="danger"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Ср. выручка с гостя</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.averageByGuest))} &#8381;
                      </span>
                      <TrendBadge arr={datasStore.averageByGuest} />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(avgVal(datasStore.averageByGuest))} &#8381;
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/avgbyguestdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  ref={widgetChartRef1}
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Ср. выручка с гостя',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-danger'),
                        data: datasStore.averageByGuest,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.averageByGuest),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4} xxl={3}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              color="primary"
              value={
                <>
                  <div className="d-flex flex-column">
                    <div className="fw-semibold">Сумма возвратов</div>
                    <div>
                      <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                      <span className="fw-semibold">
                        {fmtInt(lastVal(datasStore.dishReturnSum))} &#8381;
                      </span>
                      <TrendBadge arr={datasStore.dishReturnSum} invert />
                    </div>
                    <div className="fs-6 fw-normal">
                      За период:{" "}
                      <span className="fw-semibold">
                        {fmtInt(sumVal(datasStore.dishReturnSum))} &#8381;
                      </span>
                    </div>
                  </div>
                </>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/returndetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
              chart={
                <CChartLine
                  className="mt-3 mx-3"
                  style={{ height: '70px' }}
                  data={{
                    labels: datasStore.openDate,
                    datasets: [
                      {
                        label: 'Сумма возвратов',
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(255,255,255,.55)',
                        pointBackgroundColor: getStyle('--cui-primary'),
                        data: datasStore.dishReturnSum,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        border: {
                          display: false,
                        },
                        grid: {
                          display: false,
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                      y: {
                        ...chartAxisRange(datasStore.dishReturnSum),
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          display: false,
                        },
                      },
                    },
                    elements: {
                      line: {
                        borderWidth: 1,
                        tension: 0.4,
                      },
                      point: {
                        radius: 4,
                        hitRadius: 10,
                        hoverRadius: 4,
                      },
                    },
                  }}
                />
              }
            />
          </CCol>
        </CRow>

        <CRow className="mb-4 g-4">
          <CCol xs={12} sm={6} xl={4}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              className="dashboard-widget-indigo text-white"
              value={
                <div className="d-flex flex-column">
                  <div className="fw-semibold">Позиций на чек</div>
                  <div>
                    <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                    <span className="fw-semibold">
                      {lastDayItemsPerCheck != null ? lastDayItemsPerCheck.toFixed(2) : "—"}
                    </span>
                    <TrendBadge arr={dailyItemsPerCheck} />
                  </div>
                  <div className="fs-6 fw-normal text-white-50">
                    За период: {itemsPerCheck != null ? itemsPerCheck.toFixed(2) : "—"} ({fmtInt(totalItems)} поз. / {fmtInt(totalOrders)} чеков)
                  </div>
                </div>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/itemsdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              className="dashboard-widget-indigo text-white"
              value={
                <div className="d-flex flex-column">
                  <div className="fw-semibold">% возвратов от выручки</div>
                  <div>
                    <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                    <span className="fw-semibold">
                      {lastDayReturnPct != null ? `${lastDayReturnPct.toFixed(2)}%` : "—"}
                    </span>
                    <TrendBadge arr={dailyReturnPctSeries} invert />
                  </div>
                  <div className="fs-6 fw-normal text-white-50">
                    За период: {returnPct != null ? `${returnPct.toFixed(2)}%` : "—"} ({fmtInt(totalReturns)} ₽ возвратов)
                  </div>
                </div>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/returndetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
            />
          </CCol>
          <CCol xs={12} sm={6} xl={4}>
            <CWidgetStatsA
              className="h-100"
              style={{ minHeight: 240 }}
              className="dashboard-widget-indigo text-white"
              value={
                <div className="d-flex flex-column">
                  <div className="fw-semibold">Ср. чеков в день</div>
                  <div>
                    <span className="fs-6 fw-normal">{periodCaption}:</span>{" "}
                    <span className="fw-semibold">
                      {fmtInt(lastVal(datasStore.uniqOrderId))}
                    </span>
                    <TrendBadge arr={datasStore.uniqOrderId} />
                  </div>
                  <div className="fs-6 fw-normal text-white-50">
                    За период:{" "}
                    {datasStore.uniqOrderId.length > 0
                      ? fmtInt(totalOrders / datasStore.uniqOrderId.length)
                      : "—"}{" "}
                    в день ({fmtInt(totalOrders)} чеков)
                  </div>
                </div>
              }
              title=""
              action={
                <CDropdown alignment="end">
                  <CDropdownToggle color="transparent" caret={false} className="text-white p-0">
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem as={Link} to="/checkdetails" state={{ serverId: selectedServer }}>
                      Подробнее
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              }
            />
          </CCol>
        </CRow>
      </div>
      </>
      )}

    <CRow xs={{ gutter: 4 }}>
      <CCol xs={12} xl={8} xxl={6}>
        <CCard className="mb-4">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <span>Выручка <small className="text-body-secondary">(по OLAP)</small></span>
            <Link to="/dishdetails" className="btn btn-sm btn-outline-primary">Подробнее</Link>
          </CCardHeader>
          <CCardBody>
            <CChartBar
              data={{
                labels: datasStore.openDate,
                datasets: [
                  {
                    label: 'Выручка',
                    backgroundColor: '#7982f8ff',
                    data: datasStore.dishDiscountSumInt,
                  },
                ],
              }}
              labels="months"
            />
          </CCardBody>
        </CCard>
      </CCol>
      <CCol xs={12} xl={8} xxl={6}>
        <CCard className="mb-4">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <span>Соотношение чеков к позициям</span>
            <Link to="/itemsdetails" className="btn btn-sm btn-outline-primary">Подробнее</Link>
          </CCardHeader>
          <CCardBody>
            <CChartLine
              data={{
                labels: datasStore.openDate,
                datasets: [
                  {
                    label: 'Позиций в чеках',
                    backgroundColor: 'rgba(220, 220, 220, 0.2)',
                    borderColor: 'rgba(220, 220, 220, 1)',
                    pointBackgroundColor: 'rgba(220, 220, 220, 1)',
                    pointBorderColor: '#fff',
                    data: datasStore.orderItems,
                  },
                  {
                    label: 'Чеков',
                    backgroundColor: 'rgba(151, 187, 205, 0.2)',
                    borderColor: 'rgba(151, 187, 205, 1)',
                    pointBackgroundColor: 'rgba(151, 187, 205, 1)',
                    pointBorderColor: '#fff',
                    data: datasStore.uniqOrderId
                  },
                ],
              }}
            />
          </CCardBody>
        </CCard>
      </CCol>
      <CCol xs={12} xl={8} xxl={6}>
        <CCard className="mb-4">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <span>Типы оплат</span>
            <Link to="/payment-types" className="btn btn-sm btn-outline-primary">Подробнее</Link>
          </CCardHeader>
          <CCardBody>
            <CChartDoughnut
              data={{
                labels: payTypeItems.map((x) => x.label),
                datasets: [
                  {
                    backgroundColor: payTypeItems.map((x) => x.color),
                    data: payTypeItems.map((x) => x.value),
                  },
                ],
              }}
              options={{
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: { usePointStyle: true, boxWidth: 10 },
                  },
                  tooltip: {
                    callbacks: {
                      label(ctx) {
                        const v = toNum(ctx.parsed);
                        const pct = payTypesTotal > 0 && v != null ? (v / payTypesTotal) * 100 : null;
                        return ` ${fmtInt(v)} ₽${pct != null ? ` (${pct.toFixed(1)}%)` : ""}`;
                      },
                    },
                  },
                },
              }}
            />
            <div className="mt-3">
              {payTypeItems.map((it) => {
                const pct = payTypesTotal > 0 ? (it.value / payTypesTotal) * 100 : 0;
                return (
                  <div
                    key={it.label}
                    className="d-flex justify-content-between align-items-center py-1 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: it.color,
                          display: "inline-block",
                        }}
                      />
                      <span className="small">{it.label}</span>
                    </div>
                    <div className="small text-end">
                      <span className="fw-semibold">{fmtInt(it.value)} ₽</span>
                      <span className="text-body-secondary"> ({pct.toFixed(1)}%)</span>
                    </div>
                  </div>
                );
              })}
              {payTypeItems.length > 0 && (
                <div className="d-flex justify-content-between align-items-center pt-2">
                  <span className="small text-body-secondary">Итого</span>
                  <span className="small fw-semibold">{fmtInt(payTypesTotal)} ₽</span>
                </div>
              )}
            </div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol xs={12} xl={8} xxl={6}>
        <CCard className="mb-4">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <span>Продажи по категориям</span>
            <Link to="/categories" className="btn btn-sm btn-outline-primary">Подробнее</Link>
          </CCardHeader>
          <CCardBody>
            <CChartPie
              data={{
                labels: categoryItems.map((x) => x.label),
                datasets: [
                  {
                    data: categoryItems.map((x) => x.value),
                    backgroundColor: categoryItems.map((x) => x.color),
                  },
                ],
              }}
              options={{
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: { usePointStyle: true, boxWidth: 10 },
                  },
                  tooltip: {
                    callbacks: {
                      label(ctx) {
                        const v = toNum(ctx.parsed);
                        const pct =
                          categoryTotal > 0 && v != null ? (v / categoryTotal) * 100 : null;
                        return ` ${fmtInt(v)} ₽${pct != null ? ` (${pct.toFixed(1)}%)` : ""}`;
                      },
                    },
                  },
                },
              }}
            />
            <div className="mt-3">
              {categoryItems.map((it) => {
                const pct = categoryTotal > 0 ? (it.value / categoryTotal) * 100 : 0;
                return (
                  <div
                    key={it.label}
                    className="d-flex justify-content-between align-items-center py-1 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: it.color,
                          display: "inline-block",
                        }}
                      />
                      <span className="small">{it.label}</span>
                    </div>
                    <div className="small text-end">
                      <span className="fw-semibold">{fmtInt(it.value)} ₽</span>
                      <span className="text-body-secondary"> ({pct.toFixed(1)}%)</span>
                    </div>
                  </div>
                );
              })}
              {categoryItems.length > 0 && (
                <div className="d-flex justify-content-between align-items-center pt-2">
                  <span className="small text-body-secondary">Итого</span>
                  <span className="small fw-semibold">{fmtInt(categoryTotal)} ₽</span>
                </div>
              )}
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
      <DishStatsPanel
        serverId={selectedServer}
        dateFrom={appliedRange.from}
        dateTo={appliedRange.to}
        groupByRestaurant={datasStore.groupByRestaurant}
        restaurantGroup={datasStore.selectedRestaurantGroup}
      />
    </CContainer>
  )
})

export default Charts
